import { FormEvent, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { usePageTitle } from "../../hooks/usePageTitle";

export default function AdminSettings() {
  usePageTitle("Admin Account");
  const { admin } = useAuth();
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = "Current password is required";
    }

    if (!formData.newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = "Password must be at least 8 characters";
    }

    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = "New password must be different from current password";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSuccess(false);

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const response = await fetch("/api/admin/change-password", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword,
        }),
      });

      if (response.ok) {
        setSuccess(true);
        setFormData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });

        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: "smooth" });

        // Auto-hide success message after 5 seconds
        setTimeout(() => setSuccess(false), 5000);
      } else {
        const errorData = await response.json();
        setErrors({ general: errorData.error || "Failed to change password" });
      }
    } catch (error) {
      setErrors({ general: "An unexpected error occurred" });
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = (password: string): { text: string; color: string } => {
    if (password.length === 0) return { text: "", color: "" };
    if (password.length < 8) return { text: "Too short", color: "text-red-600" };

    let strength = 0;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;

    if (strength === 0) return { text: "Weak", color: "text-red-600" };
    if (strength === 1) return { text: "Fair", color: "text-yellow-600" };
    if (strength === 2) return { text: "Good", color: "text-blue-600" };
    return { text: "Strong", color: "text-green-600" };
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold mb-8">Admin Account Settings</h1>

      <div className="max-w-2xl">
        {/* Account Information (Read-only) */}
        <div className="card mb-6 p-5">
          <h2 className="text-xl font-bold mb-4">Account Information</h2>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Username
              </label>
              <input
                type="text"
                value={admin?.username || ""}
                disabled
                className="input w-full bg-gray-100 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Email
              </label>
              <input
                type="email"
                value={admin?.email || ""}
                disabled
                className="input w-full bg-gray-100 cursor-not-allowed"
              />
            </div>
            <p className="text-sm text-gray-500">
              Username and email cannot be changed. Contact system administrator if you need to update these fields.
            </p>
          </div>
        </div>

        {/* Password Change Form */}
        <div className="card p-5">
          <h2 className="text-xl font-bold mb-6">Change Password</h2>

          {success && (
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded mb-6">
              Password changed successfully! Please use your new password the next time you log in.
            </div>
          )}

          {errors.general && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-6">
              {errors.general}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Current Password */}
            <div>
              <label htmlFor="currentPassword" className="block text-sm font-medium mb-1">
                Current Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                id="currentPassword"
                value={formData.currentPassword}
                onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                className={`input w-full ${errors.currentPassword ? "border-red-500" : ""}`}
                disabled={loading}
                required
                autoComplete="current-password"
              />
              {errors.currentPassword && (
                <p className="text-red-500 text-sm mt-1">{errors.currentPassword}</p>
              )}
            </div>

            {/* New Password */}
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium mb-1">
                New Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                id="newPassword"
                value={formData.newPassword}
                onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                className={`input w-full ${errors.newPassword ? "border-red-500" : ""}`}
                disabled={loading}
                required
                autoComplete="new-password"
              />
              {errors.newPassword && (
                <p className="text-red-500 text-sm mt-1">{errors.newPassword}</p>
              )}
              {passwordStrength.text && (
                <p className={`text-sm mt-1 ${passwordStrength.color}`}>
                  Password strength: {passwordStrength.text}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Minimum 8 characters. Recommend using a mix of uppercase, lowercase, numbers, and symbols.
              </p>
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium mb-1">
                Confirm New Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                id="confirmPassword"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className={`input w-full ${errors.confirmPassword ? "border-red-500" : ""}`}
                disabled={loading}
                required
                autoComplete="new-password"
              />
              {errors.confirmPassword && (
                <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex gap-4">
              <button
                type="submit"
                disabled={loading}
                className="btn-primary disabled:opacity-50"
              >
                {loading ? "Changing Password..." : "Change Password"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setFormData({
                    currentPassword: "",
                    newPassword: "",
                    confirmPassword: "",
                  });
                  setErrors({});
                  setSuccess(false);
                }}
                className="btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>

        {/* Security Tips */}
        <div className="card bg-blue-50 border-blue-200 mt-6 p-5">
          <h3 className="font-bold mb-3">Password Security Tips</h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <span>•</span>
              <span>Use a unique password that you don't use for other accounts</span>
            </li>
            <li className="flex items-start gap-2">
              <span>•</span>
              <span>Include a mix of uppercase, lowercase, numbers, and special characters</span>
            </li>
            <li className="flex items-start gap-2">
              <span>•</span>
              <span>Avoid using personal information like names or birthdates</span>
            </li>
            <li className="flex items-start gap-2">
              <span>•</span>
              <span>Consider using a password manager to generate and store strong passwords</span>
            </li>
            <li className="flex items-start gap-2">
              <span>•</span>
              <span>Change your password regularly and immediately if you suspect it's been compromised</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
